import { inIframe } from '@/shared/lib/browser';
import { getTiktokSession } from '@/shared/thirdParty/tiktok';

import { AUTH_API } from './amplify';

const RETRY_BACKOFFS_MS = [500, 1500] as const;

export class SessionUnavailableError extends Error {
  constructor(message = 'No valid session') {
    super(message);
    this.name = 'SessionUnavailableError';
  }
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

let refreshInFlight: null | Promise<string> = null;

const attemptRefreshWithRetry = async (): Promise<string> => {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= RETRY_BACKOFFS_MS.length; attempt++) {
    try {
      return await AUTH_API.getTokenId();
    } catch (err) {
      lastErr = err;
      if (attempt < RETRY_BACKOFFS_MS.length) {
        await sleep(RETRY_BACKOFFS_MS[attempt]);
      }
    }
  }
  throw lastErr;
};

const redirectToAuth = (): void => {
  if (typeof window === 'undefined') return;

  if (window.location.pathname.startsWith('/auth')) return;

  window.location.href = '/auth';
};

export const getAuthToken = async (): Promise<string> => {
  if (inIframe()) {
    const token = await getTiktokSession();
    if (!token) {
      throw new SessionUnavailableError('TikTok iframe session unavailable');
    }
    return token;
  }

  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = attemptRefreshWithRetry()
    .catch((err) => {
      redirectToAuth();
      throw err;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
};
